// Where the Data Analysis table's per-row "View" button goes.
//
// Extracted from the table component because it is the one branch on that page with real
// consequences and no other way to observe it: this repo's jest runs the NODE environment (no
// jsdom), so a click on a rendered row cannot be simulated, and the navigation itself is a
// `window.location.href` assignment. As a pure function it is testable on its own terms.
//
// THREE DESTINATIONS, in priority order:
//
//   1. PULSE -> `pulse_analysis_v2`, unchanged. Pulse has no Spark equivalent, and keeping this
//      row action working is the reason the legacy table stayed the entry point at all.
//   2. SPARK, dashboard available -> the new Spark dashboard's Overview tab, carrying the
//      collection's own survey window as `?window=` so the reader lands on the administration they
//      clicked rather than on whichever window happens to be newest.
//   3. SPARK, no dashboard -> the legacy Tableau `data_analysis_show` view. `sparkReportUrl` is
//      absent exactly when the dashboard's own gate would refuse this user (it is stricter than the
//      page's policy — see SurveyCollectionsController#spark_report_url), so the button is never a
//      link to a 403.
//
// AUDIENCE (D-130). A Spark row opens the dashboard for its collection's `spark_audience` — the
// audience its SURVEYS carry (`surveys.audience`, the dashboard's own key), not the collection form's
// radio, which nothing keeps in step with them. Student and Family links carry `?audience=` beside
// `?window=`; Staff carries no audience param, so a Staff link is byte-identical to before.
//
// The dashboard silently opens STAFF for an audience the org has no published results for, so the
// table disables "View" instead (`sparkViewDisabledReason`) unless the audience is in the org's
// published list (`sparkAudiences`). A null `spark_audience` means the surveys disagree, and the
// row is disabled too: one audience's dashboard is not the answer for a collection that is partly
// another's. `analysisPath` is never reached for a disabled row.
//
// `spark_window_label` is null when the collection's surveys carry no parseable window or disagree
// about one; the link is then window-less and the dashboard opens on its own default. That is the
// honest outcome for an ambiguous window — never a guessed one.
export const isPulseCollection = (collection) => collection?.kind?.toLowerCase?.() === 'pulse';

const STAFF = 'staff';
const AUDIENCE_LABELS = { staff: 'Staff', student: 'Student', family: 'Family' };

const sparkAudienceOf = (collection) => (Object.keys(AUDIENCE_LABELS).includes(collection?.spark_audience) ? collection.spark_audience : null);

export const sparkViewDisabledReason = (collection, { sparkReportUrl, sparkAudiences } = {}) => {
  if (isPulseCollection(collection)) return null;

  const audience = sparkAudienceOf(collection);
  if (audience === STAFF) return null;
  if (!audience) return 'This collection has surveys for more than one audience, so there is no single dashboard to open.';

  const label = AUDIENCE_LABELS[audience];
  // No dashboard for this user: the row would fall back to the legacy view, which never served
  // Student or Family collections (GH #8372), so they stay disabled as before.
  if (!sparkReportUrl) return `The ${label} dashboard is not available to you.`;
  // D-130 — the dashboard only reads PUBLISHED collections, so an unpublished one's window is not
  // there to open: it would land on that audience's newest published window instead, saying nothing.
  // (A Staff row keeps today's behavior above.)
  if (collection?.publish !== 'yes') return `This collection's ${label} results are not published yet.`;
  if (!sparkAudiences?.includes(audience)) return `No published ${label} results yet.`;

  return null;
};

export const analysisPath = ({ collection, baseUrl, sparkReportUrl }) => {
  if (isPulseCollection(collection)) return `${baseUrl}/${collection.id}/pulse_analysis_v2`;
  if (!sparkReportUrl) return `${baseUrl}/${collection.id}/data_analysis_show`;

  const params = new URLSearchParams();
  const windowLabel = collection?.spark_window_label;
  if (windowLabel) params.set('window', windowLabel);

  const audience = sparkAudienceOf(collection);
  if (audience && audience !== STAFF) params.set('audience', audience);

  const query = params.toString();
  return query ? `${sparkReportUrl}?${query}` : sparkReportUrl;
};
